import { ARENA } from '../../config/constants.js';

const ORBIT_MIN_RANGE = 90; // moves outward if closer than this
const ORBIT_MAX_RANGE = 140; // moves inward if farther than this
const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08;

/**
 * Holds a mid-range band around the player instead of closing to contact —
 * corrects radially back into [ORBIT_MIN_RANGE, ORBIT_MAX_RANGE] while
 * always strafing tangentially (a per-instance random spin direction, so a
 * wave's orbiters don't all mirror each other), firing on an interval like
 * Skirmisher. Can't be tanked through the way a straight chaser can — the
 * player has to actually reposition to line up a shot or close the gap,
 * since it never walks itself into contact range on its own.
 */
export class Orbiter {
  constructor(eventBus, {
    x, y, radius = 5, hp, moveSpeed, contactDamage, shotDamage, shotSpeed, fireInterval,
  }) {
    this.eventBus = eventBus;
    this.type = 'orbiter';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this.shotDamage = shotDamage;
    this.shotSpeed = shotSpeed;
    this._fireInterval = fireInterval;
    this._fireTimer = fireInterval * Math.random();
    this._contactCooldown = 0;
    this.isDead = false;
    this.hitFlashTimer = 0;

    this._spin = Math.random() < 0.5 ? 1 : -1;
  }

  update(dt, player, bulletPool) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    const nx = dx / dist;
    const ny = dy / dist;

    let radialX = 0;
    let radialY = 0;
    if (dist > ORBIT_MAX_RANGE) {
      radialX = nx;
      radialY = ny;
    } else if (dist < ORBIT_MIN_RANGE) {
      radialX = -nx;
      radialY = -ny;
    }
    // Tangential (perpendicular to the radial direction) — always applied,
    // this is what makes it circle rather than just sit still in-band.
    const tangentX = -ny * this._spin;
    const tangentY = nx * this._spin;

    this.x += (radialX + tangentX) * this.moveSpeed * dt;
    this.y += (radialY + tangentY) * this.moveSpeed * dt;
    this._clampToArena();

    this._fireTimer -= dt;
    if (this._fireTimer <= 0) {
      this._fireTimer = this._fireInterval;
      const angle = Math.atan2(dy, dx);
      bulletPool.fire({
        x: this.x,
        y: this.y,
        vx: Math.cos(angle) * this.shotSpeed,
        vy: Math.sin(angle) * this.shotSpeed,
        radius: 2,
        damage: this.shotDamage,
        color: '#4ae0c0',
        lifetime: 4,
      });
    }

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    const appliedDamage = hpBefore - this.hp;
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: appliedDamage, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
